import React, { Suspense, lazy, useMemo, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import RequireAdminAuth, { useAdminAuth, AuthProvider } from './pages/admin/RequireAdminAuth';
import { PageLoader } from './components/common/LoadingSpinner';
import SkipLinks from './components/common/SkipLinks';
import KeyboardShortcutsHelp from './components/common/KeyboardShortcutsHelp';
import CookieConsent from './components/CookieConsent';
import NotificationBell from './components/NotificationBell';
import RealtimeNotificationManager from './components/RealtimeNotificationManager';
import ErrorBoundary from './components/ErrorBoundary';
import { useMarketplaceConfig } from './hooks/useMarketplaceConfig';
import { ToastProvider } from './context/ToastContext';
import { OnboardingProvider, useOnboarding } from './context/OnboardingContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ActionNotificationProvider } from './components/ActionNotification';
import OnboardingTour, { OnboardingHelpButton } from './components/onboarding/OnboardingTour';
import { initErrorTracker } from './utils/errorTracker';
import { initAnalytics } from './utils/analytics';
import AnalyticsTracker from './components/common/AnalyticsTracker';
import ImpersonationBanner from './components/ImpersonationBanner';
import { preloadOnIdle } from './utils/lazyLoading';
import './App.css';
import './styles/theme.css';
import './styles/mobileOptimization.css';

// Error Tracker'ı başlat
initErrorTracker({
  interceptConsole: true,
  trackClickErrors: true,
  maxErrors: 100
});

// Analytics'i başlat (GA4)
initAnalytics();

// Route preloading - idle time'da önemli chunk'ları yükle
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(() => preloadOnIdle(), 2000);
  });
}

// Lazy loaded components for code splitting
const DashboardHome = lazy(() => import('./pages/admin/DashboardHome'));
const OrdersDashboard = lazy(() => import('./pages/admin/OrdersPanel'));
const MarketplaceSettings = lazy(() => import('./pages/admin/MarketplaceSettings'));
const MetricsDashboard = lazy(() => import('./pages/admin/MetricsDashboard'));
const ProductCatalog = lazy(() => import('./pages/admin/ProductCatalog'));
const ProductPool = lazy(() => import('./pages/ProductPool'));
const ProductPoolGeneral = lazy(() => import('./pages/ProductPoolGeneral'));
const ProductEdit = lazy(() => import('./pages/ProductEdit'));
const BundlesPage = lazy(() => import('./pages/BundlesPage'));
const BundleEditPage = lazy(() => import('./pages/BundleEditPage'));
const AutoScrapedProducts = lazy(() => import('./pages/AutoScrapedProducts'));
const LogPanel = lazy(() => import('./pages/admin/LogPanel'));
const QuestionsDashboard = lazy(() => import('./pages/QuestionsDashboard'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const AnalysisRequestPage = lazy(() => import('./pages/AnalysisRequestPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const PaymentResult = lazy(() => import('./pages/PaymentResult'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const Kvkk = lazy(() => import('./pages/legal/Kvkk'));
const RefundPolicy = lazy(() => import('./pages/legal/RefundPolicy'));
const DistanceSalesAgreement = lazy(() => import('./pages/legal/DistanceSalesAgreement'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const AccountingReportPage = lazy(() => import('./pages/AccountingReportPage'));
const ProductionReportPage = lazy(() => import('./pages/admin/ProductionReportPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const MembershipReports = lazy(() => import('./components/MembershipReports'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const DevelopersPage = lazy(() => import('./pages/DevelopersPage'));
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage'));
const RefundsDashboard = lazy(() => import('./pages/admin/RefundsDashboard'));
const ImpersonationPage = lazy(() => import('./pages/admin/ImpersonationPage'));
const DocsPage = lazy(() => import('./pages/admin/DocsPage'));
const PTTAVMPricingCalculator = lazy(() => import('./pages/admin/PTTAVMPricingCalculator'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const N11ProductUpload = lazy(() => import('./pages/n11/N11ProductUpload'));
const HepsiburadaProductUpload = lazy(() => import('./pages/hepsiburada/HepsiburadaProductUpload'));
const SuperAdminPanel = lazy(() => import('./pages/admin/SuperAdminPanel'));
const MISDashboard = lazy(() => import('./pages/admin/MISDashboard'));
const ProductResearch = lazy(() => import('./pages/admin/ProductResearch'));
const CompetitorTracker = lazy(() => import('./pages/admin/CompetitorTracker'));
const KeywordResearch = lazy(() => import('./pages/admin/KeywordResearch'));
const ProfitCalculator = lazy(() => import('./pages/admin/ProfitCalculator'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogAdmin = lazy(() => import('./pages/admin/BlogAdmin'));
const ParselSorgu = lazy(() => import('./pages/ParselSorgu'));
const StoreSyncPage = lazy(() => import('./pages/admin/StoreSyncPage'));

const PLAN_PERMISSIONS = {
  starter: ['/dashboard', '/orders', '/questions', '/settings', '/catalog', '/products', '/reports', '/subscription', '/refunds'],
  pro: ['/dashboard', '/orders', '/questions', '/catalog', '/settings', '/products', '/metrics', '/reports', '/subscription', '/refunds'],
  maker3d: ['/dashboard', '/orders', '/questions', '/catalog', '/settings', '/products', '/auto-scrape', '/metrics', '/reports', '/subscription', '/refunds'],
  enterprise: ['/dashboard', '/orders', '/settings', '/catalog', '/products', '/auto-scrape', '/questions', '/metrics', '/reports', '/subscription', '/refunds', '/mis-suite']
};

function hasPermission(session, path) {
  if (!session) return false;

  // Viewer role restriction - Sadece siparişleri görebilir
  if (session.role === 'viewer') {
    const allowedForViewer = ['/orders', '/account'];
    return allowedForViewer.some(p => path.startsWith(p));
  }

  if (session.username === 'admin' || session.role === 'admin') return true;

  const plan = session.plan;
  if (!plan) return false;
  // Enterprise has access to everything (wildcard or explicit list)
  if (plan === 'enterprise') return true;

  const allowed = PLAN_PERMISSIONS[plan] || [];
  return allowed.some(p => path.startsWith(p));
}

function sanitizeNextPath(nextPath, fallback = '/account') {
  if (!nextPath || typeof nextPath !== 'string') return fallback;
  if (!nextPath.startsWith('/')) return fallback;
  if (nextPath.startsWith('//')) return fallback;
  return nextPath;
}

function PostLoginRedirect() {
  const location = useLocation();
  const next = new URLSearchParams(location.search).get('next');
  const target = sanitizeNextPath(next, '/account');

  return <Navigate to={target} replace />;
}

function AnalysisRedirect() {
  const location = useLocation();
  const params = location.search || '';
  return <Navigate to={`/mis-suite/analysis${params}`} replace />;
}

function AdminShell() {
  const { stores, groupedConfig, activeStoreMap, addStore, updateStore, removeStore } = useMarketplaceConfig();
  const { session, logout } = useAdminAuth();
  const { shouldShowOnboarding, startTour, isActive: isTourActive } = useOnboarding();
  const { theme, toggleTheme } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Sadece yeni kayıt olan kullanıcılar için otomatik tur başlatma
  useEffect(() => {
    if (!session || isTourActive) return;

    const plan = session.plan || 'starter';
    const isNewUser = session.is_new_user === true;
    const onboardingKey = `onboarding_shown_${session.id || session.username}`;
    const onboardingShown = localStorage.getItem(onboardingKey);

    // Sadece yeni kayıt olan ve daha önce tur görmemiş kullanıcılara göster
    if (!onboardingShown && isNewUser && shouldShowOnboarding(plan, isNewUser)) {
      // 1.5 saniye sonra turu başlat (sayfa yüklenmesi için)
      const timer = setTimeout(() => {
        startTour('main', plan);
        localStorage.setItem(onboardingKey, 'true');

        // is_new_user flag'ini temizle (bir kerelik)
        sessionStorage.setItem('onboarding_started', 'true');
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [session, shouldShowOnboarding, startTour, isTourActive]);

  const activeStoreMapPlain = useMemo(() => {
    if (!activeStoreMap) {
      return undefined;
    }
    return Object.fromEntries(
      Object.entries(activeStoreMap).map(([marketplace, storeSet]) => [marketplace, Array.from(storeSet)])
    );
  }, [activeStoreMap]);

  const mainNavItems = useMemo(() => [
    { path: '/dashboard', label: 'Ana Sayfa', title: 'Komuta Merkezi', icon: 'fas fa-home' },
    { path: '/orders', label: 'Siparişler', title: 'Sipariş', icon: 'fas fa-layer-group' },
    { path: '/refunds', label: 'İadeler', title: 'İade Talepleri', icon: 'fas fa-undo-alt' },
    { path: '/questions', label: 'Sorular', title: 'Sorular', icon: 'fas fa-comments' },
    { path: '/catalog', label: 'Ürün Kataloğu', title: 'Ürün Kataloğu', icon: 'fas fa-tags' },
    { path: '/products', label: 'Ürün Havuzu', title: 'Ürün Havuzu', icon: 'fas fa-box' },
    { path: '/auto-scrape', label: 'Otomatik Kazıma', title: 'Otomatik Kazıma', icon: 'fas fa-robot' },
    { path: '/profit-calculator', label: 'Kârlılık', title: 'Kârlılık Hesaplayıcı', icon: 'fas fa-calculator' },
    { path: '/mis-suite', label: 'MIS Suite', title: 'MODELERMAN Suite', icon: 'fas fa-cube' },
    { path: '/parsel-sorgu', label: 'Parsel Sorgu', title: 'Parsel Sorgulama', icon: 'fas fa-map-marked-alt' },
  ], []);

  const secondaryNavItems = useMemo(() => [
    { path: '/reports', label: 'Raporlar', title: 'Raporlar', icon: 'fas fa-chart-bar' },
    { path: '/metrics', label: 'İzleme', title: 'İzleme', icon: 'fas fa-chart-line' },
    { path: '/team', label: 'Ekip', title: 'Ekip Yönetimi', icon: 'fas fa-users' },
    { path: '/settings', label: 'Mağaza Ayarları', title: 'Mağaza Ayarları', icon: 'fas fa-store' },
    { path: '/impersonation', label: 'Kullanıcı Girişi', title: 'Kullanıcı Olarak Giriş', icon: 'fas fa-user-secret', adminOnly: true },
    { path: '/logs', label: 'Log Paneli', title: 'Sistem Logları', icon: 'fas fa-terminal', superadminOnly: true },
    { path: '/blog-admin', label: 'Blog', title: 'Blog Yönetimi', icon: 'fas fa-blog', superadminOnly: true },
    { path: '/superadmin', label: 'Süper Admin', title: 'Kullanıcı Yönetimi', icon: 'fas fa-shield-alt', superadminOnly: true },
    { path: '/store-sync', label: 'Store Sync', title: 'Store Senkronizasyonu', icon: 'fas fa-sync-alt', superadminOnly: true },
  ], []);

  const location = useLocation();

  useEffect(() => {
    const allNavItems = [...mainNavItems, ...secondaryNavItems];
    const currentItem = allNavItems.find(item => location.pathname.startsWith(item.path));

    if (currentItem) {
      document.title = currentItem.title || currentItem.label;
    } else {
      document.title = 'Order Panel';
    }
  }, [location, mainNavItems, secondaryNavItems]);

  const isAdmin = session?.role === 'admin' || session?.username === 'admin' || session?.username === 'mehmetpekgoz';
  const isSuperAdmin = session?.role === 'superadmin' || session?.role === 'owner' || session?.username === 'mehmetpekgoz';

  const visibleMainNavItems = mainNavItems.filter(item =>
    hasPermission(session, item.path) && (!item.adminOnly || isAdmin) && (!item.superadminOnly || isSuperAdmin)
  );

  const visibleSecondaryNavItems = secondaryNavItems.filter(item =>
    hasPermission(session, item.path) && (!item.adminOnly || isAdmin) && (!item.superadminOnly || isSuperAdmin)
  );

  // Determine default redirect based on plan
  const defaultRedirect = (session?.role === 'admin' || session?.username === 'admin') ? '/dashboard' :
    session?.role === 'viewer' ? '/orders' :
      session?.plan === 'basic' ? '/questions' :
        session?.plan === 'pro' ? '/questions' : '/dashboard';

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Close user menu on route change
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Mobile bottom nav items (limited to 5 most important)
  const mobileBottomNavItems = useMemo(() => [
    { path: '/dashboard', label: 'Ana Sayfa', icon: 'fas fa-home' },
    { path: '/orders', label: 'Siparişler', icon: 'fas fa-layer-group' },
    { path: '/products', label: 'Ürünler', icon: 'fas fa-box' },
    { path: '/questions', label: 'Sorular', icon: 'fas fa-comments' },
    { path: '/settings', label: 'Ayarlar', icon: 'fas fa-cog' },
  ], []);

  const visibleMobileBottomItems = mobileBottomNavItems.filter(item =>
    hasPermission(session, item.path)
  );

  return (
    <div className="admin-shell">
      <RealtimeNotificationManager enableSound={true} />
      <ImpersonationBanner />
      <SkipLinks />

      {/* Mobile Top Header - Minimal */}
      <header className="mobile-header mobile-only" role="banner">
        <div className="mobile-header__logo">
          <img src="/logo.svg" alt="Modelerman" className="mobile-header__icon" />
          Order Panel
        </div>
        <div className="mobile-header__actions">
          <button
            onClick={toggleTheme}
            className="mobile-header__btn"
            title={theme === 'dark' ? 'Aydınlık Mod' : 'Karanlık Mod'}
            aria-label={theme === 'dark' ? 'Aydınlık Mod' : 'Karanlık Mod'}
          >
            <i className={`fas fa-${theme === 'dark' ? 'sun' : 'moon'}`}></i>
          </button>
          <NotificationBell />
          <div className="user-menu-container">
            <button
              type="button"
              className="mobile-header__btn mobile-header__user"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-label="Kullanıcı menüsü"
              aria-expanded={userMenuOpen}
            >
              <i className="fas fa-user"></i>
            </button>
            {userMenuOpen && (
              <div className="user-dropdown user-dropdown--mobile">
                <div className="user-dropdown__header">
                  <div className="user-dropdown__username">{session?.username}</div>
                  <div className="user-dropdown__plan">
                    {session?.role === 'admin' ? 'Yönetici' :
                      session?.plan === 'enterprise' ? 'Enterprise Paket' :
                        session?.plan === 'pro' ? 'Pro Paket' : 'Başlangıç Paketi'}
                  </div>
                </div>
                <div className="user-dropdown__divider"></div>
                <div className="user-dropdown__nav-section">
                  {visibleSecondaryNavItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className="user-dropdown__item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <i className={item.icon}></i> {item.label}
                    </NavLink>
                  ))}
                </div>
                <div className="user-dropdown__divider"></div>
                <NavLink to="/account" className="user-dropdown__item" onClick={() => setUserMenuOpen(false)}>
                  <i className="fas fa-user-cog"></i> Hesabım
                </NavLink>
                <NavLink to="/subscription" className="user-dropdown__item" onClick={() => setUserMenuOpen(false)}>
                  <i className="fas fa-crown"></i> Abonelik
                </NavLink>
                <button type="button" className="user-dropdown__item user-dropdown__item--danger" onClick={logout}>
                  <i className="fas fa-sign-out-alt"></i> Çıkış Yap
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Desktop Navigation */}
      <nav className="admin-nav desktop-only" id="main-navigation" role="navigation" aria-label="Ana navigasyon">
        {/* Logo */}
        <div className="admin-nav__logo">
          <img src="/logo.svg" alt="Modelerman" />
          <span>Order Panel</span>
        </div>
        {/* Main Navigation Tabs - Desktop */}
        <div className="admin-nav__tabs">
          {visibleMainNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => (isActive ? 'nav-tab is-active' : 'nav-tab')}
              aria-label={item.label}
              title={item.label}
            >
              {item.icon ? <i className={`${item.icon} nav-tab__icon`} aria-hidden="true" /> : null}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        {/* Desktop: Secondary Navigation */}
        <div className="admin-nav__session desktop-only">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={toggleTheme}
              className="nav-tab"
              style={{ minWidth: 'auto', padding: '0.55rem 0.8rem' }}
              title={theme === 'dark' ? 'Aydınlık Mod' : 'Karanlık Mod'}
            >
              <i className={`fas fa-${theme === 'dark' ? 'sun' : 'moon'}`}></i>
            </button>
            <NotificationBell />
            <div className="user-menu-container" style={{ position: 'relative' }}>
              <button
                type="button"
                className="admin-nav__user"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                title={session?.username || ''}
              >
                <span className="admin-nav__username">{session?.username || 'Kullanıcı'}</span> <i className="fas fa-caret-down"></i>
              </button>
              {userMenuOpen && (
                <div className="user-dropdown">
                  <div className="user-dropdown__header">
                    <div className="user-dropdown__username">{session?.username}</div>
                    <div className="user-dropdown__plan">
                      {session?.role === 'admin' ? 'Yönetici' :
                        session?.plan === 'enterprise' ? 'Enterprise Paket' :
                          session?.plan === 'pro' ? 'Pro Paket' : 'Başlangıç Paketi'}
                    </div>
                  </div>
                  <div className="user-dropdown__divider"></div>

                  {/* Secondary Nav Items (moved from top bar) */}
                  <div className="user-dropdown__nav-section">
                    {visibleSecondaryNavItems.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className="user-dropdown__item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        {item.icon ? <i className={item.icon}></i> : null} {item.label}
                      </NavLink>
                    ))}
                  </div>
                  <div className="user-dropdown__divider"></div>

                  <NavLink to="/account" className="user-dropdown__item" onClick={() => setUserMenuOpen(false)}>
                    <i className="fas fa-user-cog"></i> Hesabım
                  </NavLink>
                  <NavLink to="/subscription" className="user-dropdown__item" onClick={() => setUserMenuOpen(false)}>
                    <i className="fas fa-crown"></i> Abonelik
                  </NavLink>
                  {session?.username === 'mehmetpekgoz' && (
                    <NavLink to="/membership-reports" className="user-dropdown__item" onClick={() => setUserMenuOpen(false)}>
                      <i className="fas fa-users-cog"></i> Üyelik Raporları
                    </NavLink>
                  )}
                  {session?.username === 'mehmetpekgoz' && (
                    <NavLink to="/production-report" className="user-dropdown__item" onClick={() => setUserMenuOpen(false)}>
                      <i className="fas fa-chart-bar"></i> Üretim Raporu
                    </NavLink>
                  )}
                  {(session?.role === 'admin' || session?.username === 'mehmetpekgoz') && (
                    <NavLink to="/docs" className="user-dropdown__item" onClick={() => setUserMenuOpen(false)}>
                      <i className="fas fa-book"></i> Dokümantasyon
                    </NavLink>
                  )}
                  <button type="button" className="user-dropdown__item" onClick={logout}>
                    <i className="fas fa-sign-out-alt"></i> Çıkış Yap
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main id="main-content" tabIndex={-1}>
        <Routes>
          <Route
            path="/dashboard"
            element={<DashboardHome />}
          />
          <Route
            path="/orders"
            element={hasPermission(session, '/orders') ? <OrdersDashboard marketplaceConfig={groupedConfig} activeStoreMap={activeStoreMapPlain} /> : <Navigate to={defaultRedirect} replace />}
          />
          <Route
            path="/settings"
            element={
              <MarketplaceSettings
                stores={stores}
                onAddStore={addStore}
                onUpdateStore={updateStore}
                onRemoveStore={removeStore}
              />
            }
          />
          <Route path="/catalog" element={hasPermission(session, '/catalog') ? <ProductCatalog marketplaceConfig={groupedConfig} /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/products" element={
            hasPermission(session, '/products')
              ? (session?.plan === 'maker3d' || session?.plan === 'enterprise' || session?.username === 'mehmetpekgoz'
                ? <ProductPool />
                : <ProductPoolGeneral />)
              : <Navigate to={defaultRedirect} replace />
          } />
          <Route path="/products/:id" element={hasPermission(session, '/products') ? <ProductEdit /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/bundles" element={hasPermission(session, '/products') ? <BundlesPage /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/bundles/:id" element={hasPermission(session, '/products') ? <BundleEditPage /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/auto-scrape" element={hasPermission(session, '/auto-scrape') ? <AutoScrapedProducts /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/logs" element={(session?.role === 'superadmin' || session?.role === 'owner' || session?.username === 'mehmetpekgoz') ? <LogPanel /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/superadmin" element={session?.username === 'mehmetpekgoz' ? <SuperAdminPanel /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/questions" element={hasPermission(session, '/questions') ? <QuestionsDashboard /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/refunds" element={hasPermission(session, '/refunds') ? <RefundsDashboard marketplaceConfig={groupedConfig} /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/impersonation" element={(session?.role === 'admin' || session?.username === 'mehmetpekgoz') ? <ImpersonationPage /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/docs" element={(session?.role === 'admin' || session?.username === 'mehmetpekgoz') ? <DocsPage /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/pttavm-pricing" element={<PTTAVMPricingCalculator />} />
          <Route path="/profit-calculator" element={<ProfitCalculator />} />
          <Route path="/blog-admin" element={session?.username === 'mehmetpekgoz' ? <BlogAdmin /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/metrics" element={hasPermission(session, '/metrics') ? <MetricsDashboard /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/reports" element={hasPermission(session, '/reports') ? <ReportsPage /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/accounting" element={hasPermission(session, '/reports') ? <AccountingReportPage /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/production-report" element={session?.username === 'mehmetpekgoz' ? <ProductionReportPage /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/invoices" element={hasPermission(session, '/reports') ? <InvoicesPage /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/n11-upload" element={<N11ProductUpload stores={stores} />} />
          <Route path="/hb-upload" element={<HepsiburadaProductUpload />} />
          <Route path="/membership-reports" element={session?.username === 'mehmetpekgoz' ? <MembershipReports /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/mis-suite" element={session?.username === 'mehmetpekgoz' ? <MISDashboard /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/mis-suite/analysis" element={(session?.plan === 'enterprise' || session?.username === 'mehmetpekgoz') ? <AnalysisRequestPage /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/parsel-sorgu" element={<RequireAdminAuth><Suspense fallback={<PageLoader />}><ParselSorgu /></Suspense></RequireAdminAuth>} />
          <Route path="/store-sync" element={(session?.role === 'admin' || session?.username === 'mehmetpekgoz') ? <Suspense fallback={<PageLoader />}><StoreSyncPage /></Suspense> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/mis-suite/products" element={session?.username === 'mehmetpekgoz' ? <ProductResearch /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/mis-suite/competitors" element={session?.username === 'mehmetpekgoz' ? <CompetitorTracker /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/mis-suite/keywords" element={session?.username === 'mehmetpekgoz' ? <KeywordResearch /> : <Navigate to={defaultRedirect} replace />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/" element={<Navigate to={defaultRedirect} replace />} />
          <Route path="*" element={<Navigate to={defaultRedirect} replace />} />
        </Routes>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-bottom-nav mobile-only" role="navigation" aria-label="Mobil navigasyon">
        {visibleMobileBottomItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => `mobile-bottom-nav__item ${isActive ? 'is-active' : ''}`}
            aria-label={item.label}
          >
            <i className={item.icon} aria-hidden="true" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AnalyticsTracker />
        <ThemeProvider>
          <ToastProvider>
            <ActionNotificationProvider>
              <AuthProvider>
                <OnboardingProvider>
                  <CookieConsent />
                  <KeyboardShortcutsHelp />
                  <OnboardingTour />
                  <OnboardingHelpButton />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/analysis" element={
                        <AnalysisRedirect />
                      } />
                      <Route path="/analysis-request" element={
                        <AnalysisRedirect />
                      } />
                      <Route path="/payment" element={
                        <RequireAdminAuth>
                          <PaymentPage />
                        </RequireAdminAuth>
                      } />
                      <Route path="/payment/success" element={<PaymentResult />} />
                      <Route path="/payment/failure" element={<PaymentResult />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/terms-of-service" element={<TermsOfService />} />
                      <Route path="/kvkk" element={<Kvkk />} />
                      <Route path="/refund-policy" element={<RefundPolicy />} />
                      <Route path="/distance-sales-agreement" element={<DistanceSalesAgreement />} />
                      <Route path="/coming-soon" element={<ComingSoon />} />
                      <Route path="/faq" element={<FAQPage />} />
                      <Route path="/tools" element={<ToolsPage />} />
                      <Route path="/tools/:toolId" element={<ToolsPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/verify-email" element={<VerifyEmailPage />} />
                      <Route path="/developers" element={<DevelopersPage />} />
                      <Route path="/api-docs" element={<ApiDocsPage />} />
                      <Route path="/blog" element={<BlogPage />} />
                      <Route path="/blog/:slug" element={<BlogPage />} />
                      <Route path="/login" element={
                        <RequireAdminAuth>
                          <PostLoginRedirect />
                        </RequireAdminAuth>
                      } />
                      <Route path="/*" element={
                        <RequireAdminAuth>
                          <AdminShell />
                        </RequireAdminAuth>
                      } />
                    </Routes>
                  </Suspense>
                </OnboardingProvider>
              </AuthProvider>
            </ActionNotificationProvider>
          </ToastProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
